var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { assertNever, fromPairs, isDefined, jsonStableStringify, toNumber, } from '@freelancer/utils';
import * as Rx from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { compareMultipleFields, flatMap, isEqual, mapValues, partition, sameElements, setDiff, toObservable, } from './helpers';
import { emptyQueryObject, isInequalityParam, Query, } from './query';
function paramMatchesdocumentWithMetadata(documentWithMetadata, param) {
    if (param.name in documentWithMetadata.rawDocument) {
        var documentValue_1 = documentWithMetadata.rawDocument[param.name];
        switch (param.condition) {
            case '==':
                return isEqual(documentValue_1, param.value);
            case '<':
                return documentValue_1 < param.value;
            case '<=':
                return documentValue_1 <= param.value;
            case '>=':
                return documentValue_1 >= param.value;
            case '>':
                return documentValue_1 > param.value;
            case 'in':
                return param.values.some(function (value) { return isEqual(documentValue_1, value); });
            case 'equalsIgnoreCase':
                return (param.value.toUpperCase() === String(documentValue_1).toUpperCase());
            case 'includes':
                return documentValue_1.some(function (value) { return isEqual(param.value, value); });
            case 'intersects':
                return param.values.some(function (paramValue) {
                    return documentValue_1.some(function (value) { return isEqual(paramValue, value); });
                });
            case 'equalsIgnoreOrder':
                return param.values.every(function (paramValue) {
                    return documentValue_1.includes(paramValue);
                });
            case 'nearby': {
                var distance = haversineDistance(param.value, documentValue_1);
                return distance <= param.value.range;
            }
            default:
                return assertNever(param);
        }
    }
    return false;
}
/**
 * Checks if an document matches a query's params and returns the mismatched ones
 * If there are no params then it does so trivially.
 */
export function getMismatchedQueryParams(documentWithMetadata, queryParams) {
    if (!queryParams) {
        return [];
    }
    var mismatchedParams = Object.values(queryParams).filter(function (clauses) {
        if (!clauses) {
            return false;
        }
        return isValidInterval(clauses)
            ? !clauses.every(function (param) {
                return paramMatchesdocumentWithMetadata(documentWithMetadata, param);
            })
            : !clauses.some(function (param) {
                return paramMatchesdocumentWithMetadata(documentWithMetadata, param);
            });
    });
    return mismatchedParams;
}
export function documentWithMetadataMatchesQueryParams(documentWithMetadata, queryParams) {
    return (getMismatchedQueryParams(documentWithMetadata, queryParams).length === 0);
}
/** Checks if query params form a valid bounded interval like a < x < b */
function isValidInterval(params) {
    var firstParam = params[0], secondParam = params[1];
    return (firstParam &&
        secondParam &&
        isInequalityParam(firstParam) &&
        isInequalityParam(secondParam) &&
        firstParam.condition.startsWith('>') &&
        secondParam.condition.startsWith('<') &&
        firstParam.value <= secondParam.value);
}
/**
 * Gets a list of documents which match a particular query. If the data isn't
 * available for a particular query it will return the "best guess" by applying
 * the query to the default list in the store.
 *
 * Note: Not for general use - only intended for datastore internals.
 *
 * @param storeSlice the object after collection and authUid has been indexed
 * @param ref the reference for the requested data.
 * @param defaultOrder the default order of the collection
 *
 * @returns An array of documents, or undefined if none matched
 */
export function selectDocumentsForQuery(storeSlice, ref, defaultOrder) {
    if (!storeSlice) {
        return undefined;
    }
    var queryString = stringifyQuery(ref.query, ref.order);
    // If queried list exists, return that.
    if (storeSlice.queries[queryString]) {
        return storeSlice.queries[queryString].ids.map(function (id) { return storeSlice.documents[id].rawDocument; });
    }
    // If the query is a search query and it's not in the store then don't try guess
    if (isSearchQuery(ref.query)) {
        return undefined;
    }
    // New query, so filter default list first.
    if ((!ref.order || isEqual(ref.order, defaultOrder)) &&
        storeSlice.queries.default) {
        var matchingObjects = storeSlice.queries.default.ids
            .map(function (id) { return storeSlice.documents[id]; })
            .filter(isDefined)
            .filter(function (documentWithMetadata) {
            return documentWithMetadataMatchesQueryParams(documentWithMetadata, ref.query && ref.query.queryParams);
        })
            .slice(0, ref.query && ref.query.limit)
            .map(function (documentWithMetadata) { return documentWithMetadata.rawDocument; });
        return matchingObjects.length > 0 ? matchingObjects : undefined;
    }
    return undefined;
}
export function stringifyQuery(query, order) {
    if (query) {
        var queryParams = query.queryParams, searchQueryParams = query.searchQueryParams, limit = query.limit, offset = query.offset;
        // FIXME: Refactor to be a tagged union
        if (!queryParams && !isSearchQuery(query)) {
            // null query - this should never appear in the store
            return 'null';
        }
        var stringifiedQueryParams = queryParams
            ? flatMap(Object.entries(queryParams), function (_a) {
                var key = _a[0], clauses = _a[1];
                return clauses.map(function (param) {
                    // FIXME
                    // When stringifying pick something that can be concatenated without ambiguity
                    switch (param.condition) {
                        case '==':
                        case '<=':
                        case '<':
                        case '>=':
                        case '>':
                            return {
                                key: key,
                                condition: param.condition,
                                value: param.value,
                            };
                        case 'equalsIgnoreCase':
                            return { key: key, condition: '~ignoreCase~', value: param.value };
                        case 'equalsIgnoreOrder':
                            return {
                                key: key,
                                condition: '~ignoreOrder~',
                                value: param.values,
                            };
                        case 'includes':
                            return { key: key, condition: '~includes~', value: param.value };
                        case 'in':
                            return { key: key, condition: '~in~', value: param.values };
                        case 'intersects':
                            return {
                                key: key,
                                condition: '~intersects~',
                                value: param.values,
                            };
                        case 'nearby':
                            return { key: key, condition: '~nearby~', value: param.value };
                        default:
                            return assertNever(param); // FIXME
                    }
                });
            })
            : [];
        var stringifiedSearchQueryParams = searchQueryParams
            ? Object.entries(searchQueryParams).map(function (_a) {
                var key = _a[0], searchQueryParam = _a[1];
                return ({
                    key: key,
                    condition: "~searchParam~",
                    value: searchQueryParam.toString(),
                });
            })
            : [];
        var stringifiedOrder = order
            ? order.map(function (ordering, index) { return ({
                key: ordering.field.toString(),
                condition: "~order" + index + "~",
                value: ordering.direction,
            }); })
            : [];
        var stringifiedPagination = Object.entries({
            limit: limit,
            offset: offset,
        })
            .map(function (_a) {
            var key = _a[0], value = _a[1];
            return ({ key: key, condition: '==', value: value });
        })
            .filter(function (_a) {
            var key = _a.key, condition = _a.condition, value = _a.value;
            return value !== undefined;
        });
        var entries = stringifiedPagination.concat(stringifiedQueryParams, stringifiedSearchQueryParams, stringifiedOrder).map(function (_a) {
            var key = _a.key, condition = _a.condition, value = _a.value;
            return "" + key + condition + jsonStableStringify(value, true);
        })
            .sort()
            .join(';');
        return entries || 'default';
    }
    // undefined query - this can happen for no-query #collection calls or #object(s) calls
    return 'default';
}
/**
 * Turns a query function passed by datastore clients to an Observable of
 * its params and other properties (limit, order).
 *
 * There are 3 cases:
 * - queryFn is `undefined` or default (query => query) -> params to `{}`
 * - queryFn contains explicit clauses (where, limit, orderBy) -> params passed through
 *   - 'where' clauses with an empty 'in' parameter (i.e. `[]`) are filtered out
 * - queryFn is the null query -> params to `undefined`
 *
 * @returns flattened query used by #collection in the datastore
 */
export function flattenQuery(queryFn) {
    var queryObject$ = queryFn
        ? toObservable(queryFn(Query.newQuery())).pipe(switchMap(function (q) { return q.query$; }))
        : Rx.of(emptyQueryObject);
    return queryObject$.pipe(map(function (query) { return (__assign({}, query, { isDocumentQuery: false, queryParams: removeInvalidQueryParams(query.queryParams) })); }), distinctUntilChanged(function (x, y) { return stringifyQuery(x, x.order) === stringifyQuery(y, y.order); }));
}
export function isSearchQuery(query) {
    return (query &&
        (query.searchQueryParams !== undefined &&
            Object.keys(query.searchQueryParams).length !== 0));
}
/** #document queries (by secondary ID) are not considered plain document references */
export function isPlainDocumentRef(ref) {
    return ref.query === undefined;
}
/**
 * Any #document call, whether by primary or secondary ID, is considered a
 * document reference. If made by primary ID, query is undefined. If made by
 * secondary ID, query is defined but isDocumentQuery is true.
 */
export function isDocumentRef(ref) {
    return ref.query === undefined || ref.query.isDocumentQuery;
}
/**
 * Removes `in` clauses from parameters where the value is an empty array `[]`.
 * If this would result in no parameters remaining, return `undefined`
 * instead of the empty params object `{}`.
 *
 * The purpose of this is to achieve the same thing as the null query, making the
 * collection Observable emit an empty list immediately without triggering a
 * network request. E.g. `query.where('id', 'in', [])` => `[]` immediately.
 *
 * To avoid this behaviour, you must explicitly check for an empty 'in' clause,
 * by chaining `notEmpty` or otherwise.
 */
// TODO: Consider moving this elsewhere to keep `flattenQuery` focused, maybe
// a separate query object transformation step
export function removeInvalidQueryParams(queryParams) {
    // Don't touch params from a null query or default query
    if (!queryParams || Object.keys(queryParams).length === 0) {
        return queryParams;
    }
    var validParamsEntries = Object.entries(queryParams).filter(function (_a) {
        var key = _a[0], clauses = _a[1];
        if (!clauses) {
            return false;
        }
        var clausesWithoutEmptyIns = clauses.filter(function (param) { return !(param.condition === 'in' && param.values.length === 0); });
        return clausesWithoutEmptyIns.length > 0;
    });
    // TODO: Convert this to fromPairs
    var parsed = validParamsEntries.reduce(function (parsedParams, _a) {
        var _b;
        var key = _a[0], param = _a[1];
        return (__assign({}, parsedParams, (_b = {}, _b[key] = param, _b)));
    }, {});
    return Object.keys(parsed).length > 0 ? parsed : undefined;
}
/**
 * Merges the unwrapped documents of a collection, only overwriting the ones
 * that have changed.
 */
export function mergeRawDocuments(currentDocuments, newDocuments) {
    // The `newDocuments`, keeping the unwrapped object from `currentDocuments` but
    // with updated metadata (timeFetched/updated) if they compare deep equal,
    var updatedNewDocuments = mapValues(newDocuments, function (newDocument, key) {
        return key in currentDocuments &&
            isEqual(currentDocuments[key].rawDocument, newDocument.rawDocument)
            ? __assign({}, newDocument, { rawDocument: currentDocuments[key].rawDocument }) : newDocument;
    });
    return __assign({}, currentDocuments, updatedNewDocuments);
}
/**
 * Merges a number of documents into the store state.
 * The `state` parameter refers to a single slice of state corresponding to one
 * collection or "feature", e.g. `users`, `projects`, etc.
 *
 * New documents are always merged with existing documents. All lists in the slice
 * are then updated:
 * - Each list corresponds to a query object. If a datastore call does not have
 *   a query it corresponds the `default` list, e.g. plain `document` calls.
 * - The current list (as determined by the query) is overwritten entirely for
 *   queried calls, and merged for non-queried calls.
 * - All other lists are merged.
 *
 * We also allow a custom sorting function to allow resorting the list.
 */
export function mergeDocuments(state, rawDocuments, order, ref, approximateTotalCount) {
    var _a, _b;
    var documents = addDocumentMetadata(rawDocuments);
    var originalUserCollectionSlice = state[ref.path.authUid];
    var userCollectionSlice = originalUserCollectionSlice && originalUserCollectionSlice.documents
        ? originalUserCollectionSlice
        : {
            documents: {},
            queries: {},
        };
    var queryString = isPlainDocumentRef(ref)
        ? undefined
        : stringifyQuery(ref.query, ref.order);
    var updatedDocuments = mergeRawDocuments(userCollectionSlice.documents, documents);
    /**
     * The ordering of a search result is generally determined as a similarity score
     * based on the query being performed. Since this is dependent on the query
     * being performed it doesn't make sense to store this score in the document,
     * and so instead we need to trust the ordering from the backend.
     *
     * Since we can neither evaluate if a document matches a search query,
     * nor compute this similarity score used for the ordering we cannot support
     * live updates for search queries.
     */
    var newListIds = isSearchQuery(ref.query)
        ? rawDocuments.map(function (document) { return document.id.toString(); })
        : Object.keys(documents)
            .filter(function (key) {
            var mismatched = getMismatchedQueryParams(documents[key], ref.query ? ref.query.queryParams : undefined);
            // complicated error display logic because we want to be as useful as possible
            if (mismatched.length > 0) {
                var invalidDocumentMessage = "Attempted to merge invalid document into list for " + ref.path.collection + ": fields " + jsonStableStringify(
                // stringify + sort so this looks the same every time for Sentry
                mismatched.map(function (params) { return params && params[0].name; }).sort()) + " in backend object did not match query.";
                // display console error with extra information for debugging
                console.error(invalidDocumentMessage, queryString, documents[key].rawDocument);
                // print each of the wrong params
                mismatched.forEach(function (params) {
                    if (params) {
                        console.error("Expected \"" + params[0].name + "\" to match " + jsonStableStringify(params) + " but was actually \"" + jsonStableStringify(documents[key].rawDocument[params[0].name]) + "\"");
                    }
                });
                // only throw 10% of the time in case we are using this "legitimately"
                if (toNumber(ref.path.authUid) % 10 === 9) {
                    throw new Error(invalidDocumentMessage);
                }
            }
            return mismatched.length === 0;
        })
            .sort(function (id1, id2) {
            return compareMultipleFields(order)(updatedDocuments[id1].rawDocument, updatedDocuments[id2].rawDocument);
        });
    var otherQueryResultsUpdated = fromPairs(Object.entries(userCollectionSlice.queries)
        .filter(function (_a) {
        var key = _a[0];
        return key !== queryString;
    })
        .map(function (_a) {
        var key = _a[0], queryResult = _a[1];
        return [
            key,
            updateDocuments(queryResult, documents, updatedDocuments),
        ];
    }));
    var query = ref.query || {
        queryParams: {},
        searchQueryParams: {},
        isDocumentQuery: false,
    };
    // For plain `document` calls, merge all lists. For queried datastore calls, e.g.
    // `collection` and `document` by secondary ID, overwrite the current list
    // and merge all other lists.
    var updatedLists = queryString
        ? __assign({}, otherQueryResultsUpdated, (_a = {}, _a[queryString] = { ids: newListIds, query: query, order: order, approximateTotalCount: approximateTotalCount }, _a)) : otherQueryResultsUpdated;
    return __assign({}, state, (_b = {}, _b[ref.path.authUid] = {
        documents: updatedDocuments,
        queries: updatedLists,
    }, _b));
}
/* This merges a number of documents into the state.
 * If an offset it specified then the new documents are placed at that offset.
 * If an offset is not specified then the new documents will be:
 *   - merged if they have the same id as an existing document OR
 *   - placed at the start of the list (and the offset of existing items moved up)
 *
 * We also allow a custom sorting function to allow resorting the list.
 * If this is used all the offsets will be then be reindexed from 0.
 * TODO T40096: This is a websocket version of mergeDocuments, which has more
 * logic right now to apply the websocket data on the right place of the store
 * slice. Maybe we can merge this with mergeDocuments again?
 */
export function mergeWebsocketDocuments(state, rawDocuments, ref) {
    var _a;
    var documents = addDocumentMetadata(rawDocuments);
    var slice = state[ref.path.authUid];
    var storeSlice = slice && slice.documents
        ? slice
        : {
            documents: {},
            queries: {},
        };
    var updatedDocuments = mergeRawDocuments(storeSlice.documents, documents);
    if (!storeSlice.queries) {
        return state;
    }
    var updatedQueryResult = fromPairs(Object.entries(storeSlice.queries).map(function (_a) {
        var key = _a[0], list = _a[1];
        return [
            key,
            updateDocuments(list, documents, updatedDocuments),
        ];
    }));
    return __assign({}, state, (_a = {}, _a[ref.path.authUid] = {
        documents: updatedDocuments,
        queries: __assign({}, storeSlice.queries, updatedQueryResult),
    }, _a));
}
function updateDocuments(queryResult, documents, updatedDocuments) {
    var query = queryResult.query, order = queryResult.order;
    var queryParams = query.queryParams, offset = query.offset, limit = query.limit;
    // If the offset is non-zero we don't add it to the new list.
    if (offset) {
        return queryResult;
    }
    // If the query is a search query we can't process it so we leave the list unchanged
    if (isSearchQuery(query)) {
        return queryResult;
    }
    // Existing document ids of the old list
    var currDocumentIds = queryResult ? Array.from(queryResult.ids) : [];
    // Find which documents modified by the websocket now match (or don't match)
    // the query. These should be added (or removed) from the list
    var _a = partition(Object.entries(documents), function (_a) {
        var documentId = _a[0], documentWithMetadata = _a[1];
        return documentWithMetadataMatchesQueryParams(documentWithMetadata, queryParams);
    }).map(function (entityEntries) { return entityEntries.map(function (_a) {
        var documentId = _a[0];
        return documentId;
    }); }), matchingIds = _a[0], nonMatchingIds = _a[1];
    // The new list is the old list plus any matching document ids, minus any
    // non-matching document ids, while ensuring all ids are unique
    var filteredDocumentIds = setDiff(currDocumentIds.concat(matchingIds), nonMatchingIds);
    // Finally, apply limit to the ids before merging into list
    var updatedDocumentIds = filteredDocumentIds.slice().sort(function (id1, id2) {
        return compareMultipleFields(order)(updatedDocuments[id1].rawDocument, updatedDocuments[id2].rawDocument);
    })
        .slice(0, limit);
    return __assign({}, queryResult, { ids: updatedDocumentIds });
}
export function addWebsocketDocuments(state, list, transform, ref, extra) {
    return mergeWebsocketDocuments(state, transformOptionalWebsocketObjects(list, transform, extra), ref);
}
/**
 * Update documents in the collection by running a transformation on them.
 * If an document is not found in the store, it is ignored.
 *
 * @param state slice of store state representing a collection
 * @param list ids of the documents to update
 * @param transform applied to every document with an id in `list`
 * @param ref a Datastore reference, links back to the dispatching action
 */
export function updateWebsocketDocuments(state, list, transform, ref) {
    return mergeWebsocketDocuments(state, transformOptionalWebsocketObjects(list, function (id) {
        var document = pluckDocumentFromRawStoreCollectionState(state, ref.path, id);
        return document ? transform(document) : undefined;
    }), ref);
}
/**
 * Update all documents in the collection by running a transformation on them.
 * The transform function should return the original object if it does not need
 * to be updated.
 *
 * Note: Due to performance considerations, you should strongly favour
 * `updateWebsocketDocuments` over this variant. This variant should only be used
 * when access to ALL the documents is required to perform an update (e.g.
 * filtering them based on some property of the document).
 * If you are unsure, use the other one.
 *
 * @param state store state representing a collection
 * @param transform applied to every document in the collection
 * @param ref a Datastore reference, links back to the dispatching action
 */
export function updateAllDocuments(state, transform, ref) {
    var slice = state[ref.path.authUid];
    return mergeWebsocketDocuments(state, transformOptionalWebsocketObjects(Object.keys(slice ? slice.documents : {}), function (id) {
        var document = pluckDocumentFromRawStoreCollectionState(state, ref.path, id);
        if (!document) {
            return undefined;
        }
        var transformedObject = transform(document);
        // Transformed object is the same, skip over this document
        return transformedObject === document ? undefined : transformedObject;
    }), ref);
}
export function transformIntoDocuments(list, transform, extra) {
    if (!list) {
        return [];
    }
    var actualList = (Array.isArray(list)
        ? list
        : Object.values(list)).filter(isDefined);
    return actualList.map(function (o) { return transform(o, extra); }).filter(isDefined);
}
export function addDocumentMetadata(rawDocuments) {
    return fromPairs(rawDocuments.map(function (rawDocument) { return [
        rawDocument.id,
        {
            rawDocument: rawDocument,
            timeFetched: Date.now(),
            timeUpdated: Date.now(),
        },
    ]; }));
}
function transformOptionalWebsocketObjects(list, transform, extra) {
    return list.map(function (o) { return transform(o, extra); }).filter(isDefined);
}
export function pathsEqual(a, b) {
    return (a.collection === b.collection &&
        a.authUid === b.authUid &&
        sameElements(a.ids || [], b.ids || []));
}
export function referencesEqual(a, b) {
    return (pathsEqual(a.path, b.path) &&
        ((a.query === undefined && b.query === undefined) ||
            (a.query !== undefined &&
                b.query !== undefined &&
                stringifyQuery(a.query, a.order) === stringifyQuery(b.query, b.order))));
}
export function pluckDocumentFromRawStore(store, path, id) {
    var slice = store[path.collection][path.authUid];
    return slice && slice.documents[id]
        ? slice.documents[id].rawDocument
        : undefined;
}
export function pluckDocumentFromRawStoreCollectionState(storeCollectionState, path, id) {
    var slice = storeCollectionState[path.authUid];
    if (!slice || !slice.documents[id]) {
        return undefined;
    }
    return slice.documents[id].rawDocument;
}
export function removeDocumentById(ref, state, id) {
    var _a;
    var data = state[ref.path.authUid] || { queries: {}, documents: {} };
    var documents = __assign({}, data.documents);
    delete documents[id];
    // Remove the deleted ID from any list
    var lists = Object.entries(data.queries).reduce(function (obj, _a) {
        var _b;
        var queryString = _a[0], list = _a[1];
        var newIds = (list && list.ids ? list.ids : []).filter(function (listId) { return listId.toString() !== id.toString(); });
        return __assign({}, obj, (_b = {}, _b[queryString] = __assign({}, list, { ids: newIds }), _b));
    }, {});
    return __assign({}, state, (_a = {}, _a[ref.path.authUid] = { documents: documents, queries: lists }, _a));
}
export function getQueryParamValue(query, name, valueSelector) {
    if (!query || !query.queryParams) {
        return [];
    }
    var clauses = query.queryParams[name];
    if (!clauses) {
        return [];
    }
    var paramValues = clauses.reduce(function (acc, param) {
        var value = valueSelector
            ? valueSelector(param)
            : isArrayParamValue(param)
                ? param.values
                : param.value;
        // 'in' + 'includes' are special cases to make actual return value consistent
        // with the return type inferred by TypeScript
        switch (param.condition) {
            case 'in':
                return acc.concat(value);
            case 'includes':
                return acc.concat([[value]]);
            default:
                return acc.concat([value]);
        }
    }, []);
    return paramValues;
}
function isArrayParamValue(param) {
    var isArrayParamCondition = param.condition === 'in' ||
        param.condition === 'intersects' ||
        param.condition === 'equalsIgnoreOrder';
    if (isArrayParamCondition && !Array.isArray(param.values)) {
        throw new Error("Query parameter on field '" + param.name + "' has condition '" + param.condition + "' but does not have an array value.");
    }
    return isArrayParamCondition;
}
export function getBoundedIntervalQueryParamValues(query, name) {
    if (!query || !query.queryParams) {
        return undefined;
    }
    var params = query.queryParams[name];
    if (!isDefined(params)) {
        return undefined;
    }
    return isValidInterval(params)
        ? [params[0].value, params[1].value]
        : undefined;
}
/**
 * Get the query param value for a nearby query and returns the first
 * nearby query param coordinate object, or undefined if the query did not
 * have the nearby condition specified.
 *
 * Note: Will throw if the given nearby query param was not valid.
 *
 * @param {RawQuery<T> | undefined} query
 * @param {K} name The key of the entity for the query with has the
 * map coordinates defined.
 *
 * @returns {NearbyQueryParamValue | undefined}
 */
export function getNearbyQueryParamValue(query, name) {
    return getQueryParamValue(query, name, function (param) {
        if (param.condition !== 'nearby') {
            return undefined;
        }
        if (typeof param.value.latitude !== 'number' ||
            typeof param.value.longitude !== 'number') {
            throw new Error('Must supply numeric latitude and longitude with nearby queries.');
        }
        return param.value;
    })[0];
}
/**
 * An implementation of the Haversine formula used to calculate "great circle"
 * distance.
 *
 * Example:
 *
 * Sydney Airport to London Heathrow Airport
 * `haversineDistance(51.469916, -0.454353, -33.939887, 151.175276) / 1000`
 * => ~17_019 km
 *
 * @see https://en.wikipedia.org/wiki/Haversine_formula
 *
 * @returns {number} Approximate distance in metres between two spatial points on Earth.
 */
export function haversineDistance(_a, _b) {
    var latDeg1 = _a.latitude, lonDeg1 = _a.longitude;
    var latDeg2 = _b.latitude, lonDeg2 = _b.longitude;
    var toRadians = function (degree) { return (degree / 180.0) * Math.PI; };
    var lat1 = toRadians(latDeg1);
    var lon1 = toRadians(lonDeg1);
    var lat2 = toRadians(latDeg2);
    var lon2 = toRadians(lonDeg2);
    // Radius of earth in meters
    var earthRadius = 6371000;
    var dLat = lat2 - lat1;
    var dLon = lon2 - lon1;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
    var c = 2 * Math.asin(Math.sqrt(a));
    return earthRadius * c;
}
